import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import FormUpload from '../../../components/formInputs/FormUpload';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IBodyTypeForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';

const CreateBodytypeForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['body-types', id],
    invalidateQueryKey: ['body-types'],
    id,
    singleGetApi: `/admin/body-types/${id}`,
    addApi: '/admin/body-types',
    editApi: `/admin/body-types/${id}`,
    itemName: t('body_type'),
    listRoute: '/body-types'
  });

  const validationSchema = Yup.object({
    code: Yup.string().required(t('required')).max(3, t('max_3')),
    description: Yup.object().shape({
      ar: Yup.string()
        .required(t('required'))
        // .matches(/^[\u0600-\u06FF\s]+$/, t('must_ar'))
        .min(3, t('name_short')),
      en: Yup.string()
        .required(t('required'))
        // .matches(/^[A-Za-z\s]+$/, t('must_en'))
        .min(3, t('name_short'))
    })
  });

  const initialValues: IBodyTypeForm = {
    ...apiData,
    
    description: {
      en: apiData?.description?.en || '',
      ar: apiData?.description?.ar || ''
    },
    
    code: apiData?.code || '',
    icon: getMedia(apiData?.media, 'icon')?.url || null
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [{ columns: 4, items: 1, type: 'textarea' }],
          [
            { columns: 2, items: 1 },
            { columns: 2, items: 2 }
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('icon')}>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload errors={formik?.errors.icon as string} touched={formik?.touched?.icon as boolean} formik={formik} name={`icon`} />
                  </FieldWrapper>
                  <FieldWrapper noPadding />
                  <FieldWrapper noPadding />
                  <FieldWrapper noPadding />
                </div>
              </SectionHeader>
              <SectionHeader title={t('body_type_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding />
                  <FieldWrapper />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`description.en`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`description.ar`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateBodytypeForm;
